// TrendModal.js
import './TrendModal.css';
import regionMap from './utils/regionMap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useMemo } from 'react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

// ----------------- helpers -----------------
const clean = (s) => (s || '').normalize('NFKC').replace(/\s+/g, ' ').trim();

function normalizeIdFromProductId(productId) {
  if (!productId) return null;
  const parts = String(productId).trim().toLowerCase().split('::');
  // Prefer brand::name if present
  const brand = clean(parts[0]);
  const name = clean(parts[1] || '');
  if (brand && name) return `${brand}::${name}`;
  return String(productId).trim().toLowerCase();
}

function normalizeIdFromRecord(rec) {
  if (rec?.productId) return normalizeIdFromProductId(rec.productId);
  const brand = clean(rec?.brand).toLowerCase();
  const name  = clean(rec?.name).toLowerCase();
  return brand && name ? `${brand}::${name}` : null;
}

function pickHistoryBlock(historicalData, key) {
  if (!historicalData || !key) return null;
  const raw = historicalData[key];
  if (!raw) return null;
  // Support both shapes: array OR { meta, history: [] }
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.history)) return raw.history;
  return null;
}

function sortByDateAsc(arr) {
  return [...arr].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function lastTwo(arr) {
  const n = arr.length;
  return n >= 2 ? [arr[n - 2], arr[n - 1]] : [null, n ? arr[0] : null];
}

// Make the last history point equal to today's target rank.
// If the target already exists in history, clip to its last occurrence;
// otherwise append a synthetic { date: today, rank: target } row.
function clipOrExtendSeriesToRank(series, targetRank, today) {
  if (!Array.isArray(series) || series.length === 0 || targetRank == null) return series || [];

  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i] && series[i].rank === targetRank) {
      return series.slice(0, i + 1);
    }
  }
  return [...series, { date: today, rank: targetRank }];
}

// ----------------- component -----------------
export default function TrendModal({
  product,
  globalData,
  historicalData,    // map of histories (recommended or best-sellers or global history)
  currentRegion,     // 'global' | 'us' | 'ca' | ...
  viewMode,          // 'recommended' | 'bestseller'
  onClose,
}) {
  if (!product) return null;

  // --- Normalized identifiers (brand::name base key) ---
  const fullId = product.productId?.trim().toLowerCase() || null;
  const baseId = normalizeIdFromRecord(product);
  const imgToken = product.image?.trim().toLowerCase() || null;

  // --- Find history: try fullId -> baseId -> image match in keys ---
  let historyArr =
    (fullId && pickHistoryBlock(historicalData, fullId)) ||
    (baseId && pickHistoryBlock(historicalData, baseId)) ||
    null;

  if (!historyArr && imgToken && historicalData) {
    const keys = Object.keys(historicalData);
    const keyByImage = keys.find(k => k.toLowerCase().includes(`::${imgToken}::`));
    historyArr = keyByImage ? pickHistoryBlock(historicalData, keyByImage) : null;
  }

  const history = Array.isArray(historyArr) ? sortByDateAsc(historyArr) : [];

  // --- Current (authoritative) global match from latest-global.json ---
  const matchById = (pId) => {
    const pid = pId?.trim().toLowerCase();
    return (
      (!!fullId && pid === fullId) ||
      (!!baseId && pid === baseId) ||
      (!!imgToken && pid?.includes(imgToken))
    );
  };
  const globalMatch = Array.isArray(globalData)
    ? globalData.find(p => matchById(p.productId))
    : null;

  // --- Basic flags & region keys ---
  const isGlobal = currentRegion === 'global';
  const regionKey = currentRegion?.toLowerCase();

  // --- Current ranks for label lines (from current list) ---
  const currentGlobalRank =
    typeof globalMatch?.globalRank === 'number' ? globalMatch.globalRank : null;

  const currentRegionalRank =
    !isGlobal
      ? (typeof product?.rank === 'number'
          ? product.rank
          : (typeof product?.bestSellingRank === 'number' ? product.bestSellingRank : null))
      : null;

  // --- Changes from last two history points ---
  const [prevH, lastH] = lastTwo(history);
  const globalChange =
    (isGlobal &&
      typeof prevH?.globalRank === 'number' &&
      typeof lastH?.globalRank === 'number')
      ? (prevH.globalRank - lastH.globalRank)
      : 0;

  const regionalChange =
    (!isGlobal && typeof product?.rankChange?.[regionKey] === 'number')
      ? product.rankChange[regionKey]
      : 0;

  // --- Build history series for this view/context ---
  const regionHistory = history
    .map(entry => {
      let rank = null;
      if (isGlobal) {
        rank = typeof entry.globalRank === 'number' ? entry.globalRank : null;
      } else if (viewMode === 'bestseller') {
        // best-sellers history sometimes stores `rank` directly, otherwise inside regionalRank[regionKey]
        rank = typeof entry.rank === 'number'
          ? entry.rank
          : (typeof entry.regionalRank?.[regionKey] === 'number'
              ? entry.regionalRank[regionKey]
              : null);
      } else {
        // recommended history
        rank = typeof entry.regionalRank?.[regionKey] === 'number'
          ? entry.regionalRank[regionKey]
          : null;
      }
      return rank != null ? { date: entry.date, rank } : null;
    })
    .filter(Boolean);

  // Local YYYY-MM-DD for "today"
  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  // If there’s no history yet for this view, fallback to a single point using today's known rank
  const targetRankForFallback = isGlobal ? currentGlobalRank : currentRegionalRank;
  const fallbackPoint =
    (regionHistory.length === 0 && typeof targetRankForFallback === 'number')
      ? [{ date: today, rank: targetRankForFallback }]
      : [];

  // Match ProductPage behavior: clip to an existing matching point, otherwise append "today"
  const targetRank = isGlobal ? currentGlobalRank : currentRegionalRank;
  const adjustedSeries =
    (typeof targetRank === 'number')
      ? clipOrExtendSeriesToRank(regionHistory, targetRank, today)
      : regionHistory;

  const effectiveHistory = adjustedSeries.length ? adjustedSeries : fallbackPoint;

  // --- Product/meta fields for the header block ---
  const {
    brand,
    name,
    price,
    status,
    regionsAvailableIn = [],
    image,
    url,
    bestSellingRank,
  } = product;

  const aggregateRank = globalMatch?.aggregateRank ?? null;
  const aggregateMethod = globalMatch?.aggregateMethod ?? null;

  // --- Render helpers ---
  const renderMovement = (change) => {
    const n = (typeof change === 'number' && isFinite(change)) ? change : 0;
    if (n > 0) return <span className="rank-up">🔺 +{n}</span>;
    if (n < 0) return <span className="rank-down">🔻 {Math.abs(n)}</span>;
    return <span className="rank-steady">⏸️ 0</span>;
  };

  // --- Chart data/options ---
  const chartData = useMemo(() => {
    const labels = effectiveHistory.map(e => e.date);
    const points = effectiveHistory.map(e => e.rank);

    const label =
      isGlobal
        ? 'Global Rank'
        : (viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank');

    return {
      labels,
      datasets: [
        {
          label,
          data: points,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [effectiveHistory, isGlobal, viewMode]);

  const options = useMemo(() => {
    const nums = effectiveHistory.map(e => e.rank).filter(v => typeof v === 'number');
    const minRank = nums.length ? Math.min(...nums) : 1;
    const maxRank = nums.length ? Math.max(...nums) : 1;

    const labelType =
      isGlobal
        ? 'Global Rank'
        : (viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank');

    return {
      responsive: true,
      plugins: {
        legend: { display: true, position: 'bottom' },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (ctx) => `${labelType}: #${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        y: {
          reverse: true,
          min: Math.max(1, minRank - 1),
          max: maxRank + 1,
          ticks: {
            precision: 0,
            stepSize: 1,
            callback: (v) => `#${v}`,
          },
          title: { display: true, text: 'Rank' },
        },
        x: {
          title: { display: true, text: 'Date' },
        },
      },
    };
  }, [effectiveHistory, isGlobal, viewMode]);

  // Force chart remount when series changes (prevents caching)
  const chartKey = useMemo(() => {
    const last = effectiveHistory[effectiveHistory.length - 1] || {};
    return `${viewMode}-${currentRegion}-${effectiveHistory.length}-${last.date}-${last.rank}`;
  }, [viewMode, currentRegion, effectiveHistory]);

  // ----------------- render -----------------
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-wide" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="modal-content-container">
          <div className="modal-image-container">
            <img src={image} alt={name} className="modal-image" />
          </div>

          <div className="modal-info">
            <h2>{name}</h2>
            <p><strong>Brand:</strong> {brand}</p>
            <p><strong>Price:</strong> {price}</p>
            <p><strong>Status:</strong> {status}</p>

            {!isGlobal && (
              <>
                <p>
                  <strong>{viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank'}:</strong>{' '}
                  {typeof currentRegionalRank === 'number'
                    ? `#${currentRegionalRank}`
                    : (viewMode === 'bestseller' && typeof bestSellingRank === 'number'
                        ? `#${bestSellingRank}`
                        : '—')}
                </p>
                <p><strong>Rank Change:</strong> {renderMovement(regionalChange)}</p>
              </>
            )}

            {isGlobal && (
              <>
                <p><strong>Global Rank:</strong> {currentGlobalRank != null ? `#${currentGlobalRank}` : '—'}</p>
                <p><strong>Global Rank Change:</strong> {renderMovement(globalChange)}</p>
                <p><strong>Aggregate Rank:</strong> {aggregateRank != null ? `#${aggregateRank}` : '—'}</p>
                <p>
                  <strong>
                    <abbr title="Calculated as an average of all available regional ranks">
                      Aggregate Method:
                    </abbr>
                  </strong>{' '}
                  {aggregateMethod || '—'}
                </p>
              </>
            )}

            {regionsAvailableIn.length > 0 && (
              <div>
                <p><span role="img" aria-label="globe">🌎</span> <strong>Available in:</strong></p>
                <div className="region-pill-container">
                  {regionsAvailableIn.map(code => (
                    <span key={code} className="region-pill">
                      {regionMap[code] || code.toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {url && (
              <p>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  View Product
                </a>
              </p>
            )}
          </div>
        </div>

        {effectiveHistory.length > 0 && (
          <div className="chart-container">
            <h3>
              {isGlobal
                ? 'Global Rank Trend'
                : viewMode === 'bestseller'
                  ? 'Best Seller Rank Trend'
                  : 'Recommended Rank Trend'}
            </h3>
            <Line options={options} data={chartData} key={chartKey} />
            {effectiveHistory.length === 1 && (
              <p style={{ marginTop: 6, color: '#666', fontSize: '0.85rem' }}>
                Only today’s rank so far — history will build over time.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}





// // TrendModal.js
// import './TrendModal.css';
// import regionMap from './utils/regionMap';
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   Tooltip as ChartTooltip,
//   Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';
// import { useMemo } from 'react';

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   ChartTooltip,
//   Legend
// );

// export default function TrendModal({
//   product,
//   globalData,
//   historicalData, // recommended OR best-sellers history file (caller decides)
//   currentRegion,
//   viewMode, // 'recommended' | 'bestseller'
//   onClose
// }) {
//   if (!product) return null;

//   // --- ID handling: support both key styles ---
//   // best-sellers history keys include image/url in productId,
//   // recommended history keys are just brand::name
//   const fullId = product.productId?.trim().toLowerCase();
//   const baseId = fullId?.split('::').slice(0, 2).join('::'); // brand::name
//   const historyRaw =
//     (historicalData?.[fullId]) ||
//     (historicalData?.[baseId]) ||
//     [];

//   // Sort history by date ascending just in case
//   const history = Array.isArray(historyRaw)
//     ? [...historyRaw].sort((a, b) => new Date(a.date) - new Date(b.date))
//     : [];

//   // Find global match for aggregate fields
//   const globalMatch = globalData?.find(
//     p => p.productId?.trim().toLowerCase() === fullId || p.productId?.trim().toLowerCase() === baseId
//   );

//   // Latest + previous entries (for changes)
//   const latestEntry = history.length > 0 ? history[history.length - 1] : {};
//   const prevEntry = history.length >= 2 ? history[history.length - 2] : null;

//   // --- Rank derivation by context ---
//   const isGlobal = currentRegion === 'global';
//   const regionKey = currentRegion?.toLowerCase();

//   const latestGlobalRank = typeof latestEntry?.globalRank === 'number' ? latestEntry.globalRank : null;

//   // Best-sellers history writes `rank` (numeric) alongside regionalRank map.
//   // Recommended history has only regionalRank map.
//   const latestBestSellerRank = typeof latestEntry?.rank === 'number' ? latestEntry.rank : null;
//   const latestRegionalRank =
//     typeof latestEntry?.regionalRank?.[regionKey] === 'number'
//       ? latestEntry.regionalRank[regionKey]
//       : null;

//   const effectiveRegionalRank = isGlobal
//     ? null
//     : (viewMode === 'bestseller'
//         ? (latestBestSellerRank ?? latestRegionalRank ?? product?.rank ?? product?.bestSellingRank ?? null)
//         : (latestRegionalRank ?? product?.rank ?? product?.bestSellingRank ?? null));

//   // Compute changes
//   const globalChange =
//     (typeof prevEntry?.globalRank === 'number' && typeof latestGlobalRank === 'number')
//       ? (prevEntry.globalRank - latestGlobalRank)
//       : 0;

//   // Prefer per-entry regional changes if you ever add them; fallback to product.rankChange
//   const rawRegionalChange =
//     (product?.rankChange?.[regionKey]) ??
//     (product?.rankChange) ??
//     0;

//   const regionalChange =
//     (typeof rawRegionalChange === 'number' && isFinite(rawRegionalChange))
//       ? rawRegionalChange
//       : 0;

//   // Product fields
//   const {
//     brand,
//     name,
//     price,
//     status,
//     regionsAvailableIn = [],
//     image,
//     url,
//     bestSellingRank,
//   } = product;

//   const aggregateRank = globalMatch?.aggregateRank;
//   const aggregateMethod = globalMatch?.aggregateMethod;

//   const renderMovement = (change) => {
//     const safeChange = typeof change === 'number' && isFinite(change) ? change : 0;
//     if (safeChange > 0) return <span className="rank-up">🔺 +{safeChange}</span>;
//     if (safeChange < 0) return <span className="rank-down">🔻 {Math.abs(safeChange)}</span>;
//     return <span className="rank-steady">⏸️ 0</span>;
//   };

//   // --- Build regionHistory used by the chart ---
//   const regionHistory = history
//     .map(entry => {
//       let baseRank = null;

//       if (isGlobal) {
//         baseRank = entry.globalRank;
//       } else if (viewMode === 'bestseller') {
//         // For best-sellers, prefer explicit entry.rank; if missing, fall back to mapped regionalRank
//         baseRank = (typeof entry.rank === 'number')
//           ? entry.rank
//           : (typeof entry.regionalRank?.[regionKey] === 'number'
//               ? entry.regionalRank[regionKey]
//               : null);
//       } else {
//         // Recommended (regional)
//         baseRank = (typeof entry.regionalRank?.[regionKey] === 'number')
//           ? entry.regionalRank[regionKey]
//           : null;
//       }

//       return {
//         date: entry.date,
//         rank: (typeof baseRank === 'number' ? baseRank : null),
//       };
//     })
//     .filter(entry => entry.rank !== null && typeof entry.rank === 'number');

//   // --- Chart.js dataset (kept exactly like your original styling) ---
//   const chartData = useMemo(() => {
//     const labels = regionHistory.map(entry => entry.date);
//     const dataPoints = regionHistory.map(entry => entry.rank);

//     const label =
//       isGlobal
//         ? 'Global Rank'
//         : (viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank');

//     return {
//       labels,
//       datasets: [
//         {
//           label,
//           data: dataPoints,
//           borderColor: 'rgba(75, 192, 192, 1)',
//           backgroundColor: 'rgba(75, 192, 192, 0.2)',
//           tension: 0.3,
//           pointRadius: 3,
//           pointHoverRadius: 6,
//         },
//       ],
//     };
//   }, [regionHistory, isGlobal, viewMode]);

//   const options = useMemo(() => {
//     const dataPoints = regionHistory.map(entry => entry.rank).filter(v => typeof v === 'number');
//     const minRank = dataPoints.length ? Math.min(...dataPoints) : 1;
//     const maxRank = dataPoints.length ? Math.max(...dataPoints) : 1;

//     const labelType =
//       isGlobal
//         ? 'Global Rank'
//         : (viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank');

//     return {
//       responsive: true,
//       plugins: {
//         legend: { display: true, position: 'bottom' },
//         tooltip: {
//           mode: 'index',
//           intersect: false,
//           callbacks: {
//             label: function(context) {
//               return `${labelType}: #${context.parsed.y}`;
//             }
//           }
//         },
//       },
//       scales: {
//         y: {
//           reverse: true,
//           min: Math.max(1, minRank - 1),
//           max: maxRank + 1,
//           ticks: {
//             precision: 0,
//             stepSize: 1,
//             callback: value => `#${value}`,
//           },
//           title: { display: true, text: 'Rank' },
//         },
//         x: {
//           title: { display: true, text: 'Date' },
//         },
//       },
//     };
//   }, [regionHistory, isGlobal, viewMode]);

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-wide" onClick={(e) => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>✕</button>

//         <div className="modal-content-container">
//           <div className="modal-image-container">
//             <img src={image} alt={name} className="modal-image" />
//           </div>

//           <div className="modal-info">
//             <h2>{name}</h2>
//             <p><strong>Brand:</strong> {brand}</p>
//             <p><strong>Price:</strong> {price}</p>
//             <p><strong>Status:</strong> {status}</p>

//             {!isGlobal && (
//               <>
//                 <p>
//                   <strong>{viewMode === 'bestseller' ? 'Best Seller Rank' : 'Recommended Rank'}:</strong>{' '}
//                   {typeof effectiveRegionalRank === 'number' ? `#${effectiveRegionalRank}` : '—'}
//                 </p>
//                 <p><strong>Rank Change:</strong> {renderMovement(regionalChange)}</p>
//                 {viewMode === 'bestseller' && typeof bestSellingRank === 'number' && (
//                   <p><strong>Best Seller Rank (current):</strong> #{bestSellingRank}</p>
//                 )}
//               </>
//             )}

//             {isGlobal && (
//               <>
//                 <p><strong>Global Rank:</strong> {latestGlobalRank !== null ? `#${latestGlobalRank}` : '—'}</p>
//                 <p><strong>Global Rank Change:</strong> {renderMovement(globalChange)}</p>
//                 <p><strong>Aggregate Rank:</strong> {aggregateRank != null ? `#${aggregateRank}` : '—'}</p>
//                 <p>
//                   <strong>
//                     <abbr title="Calculated as an average of all available regional ranks">
//                       Aggregate Method:
//                     </abbr>
//                   </strong>{' '}
//                   {aggregateMethod || '—'}
//                 </p>
//               </>
//             )}

//             {globalMatch && regionsAvailableIn.length > 0 && (
//               <div>
//                 <p><span role="img" aria-label="globe">🌎</span> <strong>Available in:</strong></p>
//                 <div className="region-pill-container">
//                   {regionsAvailableIn.map(code => (
//                     <span key={code} className="region-pill">
//                       {regionMap[code] || code.toUpperCase()}
//                     </span>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {url && (
//               <p>
//                 <a href={url} target="_blank" rel="noopener noreferrer">
//                   View Product
//                 </a>
//               </p>
//             )}
//           </div>
//         </div>

//         {regionHistory.length > 0 && (
//           <div className="chart-container">
//             <h3>
//               {isGlobal
//                 ? 'Global Rank Trend'
//                 : viewMode === 'bestseller'
//                   ? 'Best Seller Rank Trend'
//                   : 'Recommended Rank Trend'}
//             </h3>
//             <Line
//               options={options}
//               data={chartData}
//               key={viewMode + '-' + currentRegion}
//             />
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }
